import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useDebounce } from "../../../utils/hooks/useDebounce";

const LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 400;

/** One page of a list endpoint, in the `{ rows, count }` shape every list answer has. */
interface PagedRows<Row> {
  rows: Row[];
  count: number;
}

interface SearchablePagedRowsOptions<Row> {
  /** A closed dialog reads nothing; opening it loads the first page. */
  open: boolean;
  /**
   * Reads one page. The fetch depends on it, so the caller has to keep it stable
   * (`useCallback`) or every render would fire another request.
   */
  fetchRows: (
    page: number,
    limit: number,
    search?: string,
  ) => Promise<PagedRows<Row>>;
}

/**
 * The searchable, paged list the catalogue dialogs and the cost page are built on: a
 * debounced search box, paging, and the guard that keeps a slow earlier answer from
 * landing on top of a later one. Each screen keeps its own picks and its own table; only
 * this half is shared.
 */
export function useSearchablePagedRows<Row>({
  open,
  fetchRows,
}: SearchablePagedRowsOptions<Row>) {
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [rows, setRows] = useState<Row[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  // Bumped by `reload` to read the same page again after the caller changed a row.
  const [reloadCount, setReloadCount] = useState<number>(0);

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  // Typing fires one request per debounced term; without this only the newest may write,
  // so a slower earlier answer cannot land on top of a later one.
  const latestRequestRef = useRef<number>(0);

  // A term narrows the whole list, so the page it is applied to has to start at the first.
  const [appliedSearch, setAppliedSearch] = useState<string>(debouncedSearch);
  if (appliedSearch !== debouncedSearch) {
    setAppliedSearch(debouncedSearch);
    setPage(1);
  }

  useEffect(() => {
    if (!open) return;

    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setLoading(true);

    const loadPage = async () => {
      try {
        const fetched = await fetchRows(
          page,
          limit,
          debouncedSearch || undefined,
        );
        if (latestRequestRef.current !== requestId) return;
        setRows(fetched.rows);
        setTotalCount(fetched.count);
      } catch {
        // The axios interceptor reports the failure; clearing the rows shows the empty
        // state instead of leaving the reader on a skeleton that never resolves.
        if (latestRequestRef.current !== requestId) return;
        setRows([]);
        setTotalCount(0);
      } finally {
        if (latestRequestRef.current === requestId) setLoading(false);
      }
    };

    loadPage();
  }, [open, fetchRows, page, limit, debouncedSearch, reloadCount]);

  // Reopening should read as a fresh list rather than resuming the last search.
  useEffect(() => {
    if (open) return;
    setSearch("");
    setPage(1);
  }, [open]);

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const reload = () => {
    setReloadCount((count) => count + 1);
  };

  return {
    search,
    handleChangeSearch,
    page,
    setPage,
    limit,
    setLimit,
    rows,
    totalCount,
    loading,
    reload,
  };
}
